import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Unit } from '../types'
import {
  HUB_PATH,
  askLinksFromUnits,
  attributeRows,
  cardPublicPath,
  categoryPath,
  formatYen,
  jsonLd,
  marketParagraph,
  marketViewFromUnits,
  metaDescription,
} from '../seo/card_landing'
import { HONESTY_SENTENCE, PriorityCard, priorityCardBySlug } from '../seo/priority_cards'

import '../stylesheets/HighIntentCard.scss'

const DEFAULT_DESCRIPTION = 'あなたのポケモンカードゲームのための一つのショップ！あなたのカードを他のプレイヤーに販売するためにあなたのショップを設定します！'

type Props = Readonly<{
  card: PriorityCard
  units?: Unit[]
  listingsLoading: boolean
}>

const HighIntentCardLanding = ({ card, units, listingsLoading }: Props) => {
  const { t } = useTranslation()
  const market = useMemo(() => marketViewFromUnits(units, listingsLoading), [listingsLoading, units])
  const asks = askLinksFromUnits(listingsLoading ? undefined : units)

  useEffect(() => {
    const description = document.querySelector('meta[name="description"]')
    const previous = description?.getAttribute('content') ?? DEFAULT_DESCRIPTION
    description?.setAttribute('content', metaDescription(card, market))

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.id = 'high-intent-card-jsonld'
    script.text = JSON.stringify(jsonLd(card, market))
    document.head.appendChild(script)

    return () => {
      description?.setAttribute('content', previous)
      script.remove()
    }
  }, [card, market])

  const related = card.relatedSlugs.flatMap((slug) => {
    const other = priorityCardBySlug(slug)
    return other ? [other] : []
  })

  return (
    <section className='high-intent-card'>
      <p>{ card.intro }</p>
      <p className='honesty'>{ HONESTY_SENTENCE }</p>
      <h3>{ t('cards.high_intent.facts') }</h3>
      <dl>
        {
          attributeRows(card).map((row) => (
            <div key={ `${row.label}-${row.value}` }>
              <dt>{ row.label }</dt>
              <dd>{ row.value }</dd>
            </div>
          ))
        }
      </dl>
      <h3>{ t('cards.high_intent.market') }</h3>
      <p>{ marketParagraph(market) }</p>
      {
        asks.length > 0
          ? <ul className='asks'>
            {
              asks.map((ask) => (
                <li key={ ask.href }>
                  <Link to={ ask.href }>{ t(`collections.conditions.short.${ask.condition}`) }</Link>
                  <span>{ formatYen(ask.priceJpy) }</span>
                </li>
              ))
            }
          </ul>
          : <p><a href='#listings'>{ t('cards.high_intent.listings') }</a></p>
      }
      <h3>{ t('cards.high_intent.links') }</h3>
      <ul>
        {
          card.series && card.series.id !== card.set.id
            ? <li><Link to={ categoryPath(card.series.id) }>{ card.series.nameJa }</Link></li>
            : null
        }
        {
          card.printing && card.printing.id !== card.set.id
            ? <li><Link to={ categoryPath(card.printing.id) }>{ card.printing.nameJa }</Link></li>
            : null
        }
        <li><Link to={ categoryPath(card.set.id) }>{ card.set.nameJa }</Link></li>
        {
          (card.alsoIn ?? []).map((extra) => (
            <li key={ extra.id }><Link to={ categoryPath(extra.id) }>{ extra.nameJa }</Link></li>
          ))
        }
        {
          related.map((other) => (
            <li key={ other.slug }><a href={ cardPublicPath(other.slug) }>{ other.h1 }</a></li>
          ))
        }
        <li><a href={ HUB_PATH }>{ t('cards.high_intent.hub') }</a></li>
      </ul>
    </section>
  )
}

export { HighIntentCardLanding }
